import React from 'react'
import { Button, Icon } from 'antd'
import { ImagePreview } from '../../../../../../components'
import './BrandProfilePanel.scss'

const getArray = value => (Array.isArray(value) ? value : [])

const formatLabel = key =>
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase())

const isPlainObject = value =>
  value && typeof value === 'object' && !Array.isArray(value)

const normalizeText = value => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return 'Not specified'
  }

  return String(value)
}

const splitParagraphs = text =>
  normalizeText(text)
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean)

const KNOWN_PROFILE_KEYS = [
  'organization_name',
  'tagline',
  'mission',
  'brand_personality',
  'visual_identity',
  'content_guidelines',
  'video_defaults',
  'brand_values',
  'target_audience',
  'competitor_differentiation',
  'summary'
]

const SectionHeader = ({ icon, title, subtitle, count }) => (
  <div className="brand-profile-panel__section-header">
    <span className="brand-profile-panel__section-icon">
      <Icon type={icon} />
    </span>
    <div className="brand-profile-panel__section-title">
      <h3>{title}</h3>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
    {typeof count !== 'undefined' ? (
      <span className="brand-profile-panel__section-count">{count}</span>
    ) : null}
  </div>
)

const PillList = ({ items, tone = 'default' }) => {
  const values = getArray(items)

  if (!values.length) {
    return <span className="brand-profile-panel__muted">Not specified</span>
  }

  return (
    <div className={`brand-profile-panel__pills brand-profile-panel__pills--${tone}`}>
      {values.map(item => (
        <span key={item}>{item}</span>
      ))}
    </div>
  )
}

const ValueGrid = ({ items }) => {
  const values = getArray(items)

  if (!values.length) {
    return <span className="brand-profile-panel__muted">Not specified</span>
  }

  return (
    <div className="brand-profile-panel__value-grid">
      {values.map(item => (
        <div className="brand-profile-panel__value-item" key={item}>
          <span />
          {item}
        </div>
      ))}
    </div>
  )
}

const renderTextValue = value => {
  if (Array.isArray(value)) {
    return <PillList items={value.map(String)} />
  }

  if (isPlainObject(value)) {
    return <DetailGrid data={value} />
  }

  return <p>{normalizeText(value)}</p>
}

const DetailGrid = ({ data }) => {
  const entries = Object.entries(data || {})

  if (!entries.length) {
    return <span className="brand-profile-panel__muted">Not specified</span>
  }

  return (
    <div className="brand-profile-panel__detail-grid">
      {entries.map(([key, value]) => (
        <div className="brand-profile-panel__detail-item" key={key}>
          <strong>{formatLabel(key)}</strong>
          {renderTextValue(value)}
        </div>
      ))}
    </div>
  )
}

const TextCard = ({ icon, title, subtitle, count, children, className = '' }) => (
  <section className={`brand-profile-panel__card ${className}`}>
    <SectionHeader icon={icon} title={title} subtitle={subtitle} count={count} />
    <div className="brand-profile-panel__card-body">{children}</div>
  </section>
)

const ColorSwatches = ({ primaryColors, secondaryColors }) => {
  const primary = getArray(primaryColors)
  const secondary = getArray(secondaryColors)

  if (!primary.length && !secondary.length) {
    return <span className="brand-profile-panel__muted">No colors defined</span>
  }

  const renderSwatch = (color, index, isPrimary) => {
    const hex = color.hex || '#e8eaff'
    const isDark =
      parseInt(hex.replace('#', '').substring(0, 2), 16) < 140

    return (
      <div
        className={`brand-profile-panel__swatch${isPrimary ? ' brand-profile-panel__swatch--primary' : ''}`}
        key={`${hex}-${color.name || index}`}
      >
        <div
          className="brand-profile-panel__swatch-color"
          style={{ backgroundColor: hex }}
        >
          <span
            className="brand-profile-panel__swatch-hex"
            style={{ color: isDark ? '#ffffff' : '#1a1a2e' }}
          >
            {hex}
          </span>
        </div>
        <div className="brand-profile-panel__swatch-info">
          <strong>{color.name || 'Color'}</strong>
          {color.usage ? <small>{color.usage}</small> : null}
        </div>
      </div>
    )
  }

  return (
    <div className="brand-profile-panel__swatches">
      {primary.length ? (
        <div className="brand-profile-panel__swatch-group">
          <div className="brand-profile-panel__swatch-label">Primary</div>
          <div className="brand-profile-panel__swatch-row">
            {primary.map((c, i) => renderSwatch(c, i, true))}
          </div>
        </div>
      ) : null}
      {secondary.length ? (
        <div className="brand-profile-panel__swatch-group">
          <div className="brand-profile-panel__swatch-label">Secondary</div>
          <div className="brand-profile-panel__swatch-row">
            {secondary.map((c, i) => renderSwatch(c, i, false))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

const TypographyDisplay = ({ typography }) => {
  const bodyFont = typography?.body_font || typography?.body || typography?.font
  const headingFont = typography?.heading_font || typography?.heading || bodyFont
  const fallbackFonts = typography?.fallback_fonts || typography?.fallbacks

  return (
    <div className="brand-profile-panel__typography">
      <div className="brand-profile-panel__type-specimen">
        <span className="brand-profile-panel__type-label">Heading</span>
        <div
          className="brand-profile-panel__type-preview brand-profile-panel__type-preview--heading"
          style={{ fontFamily: headingFont || 'inherit' }}
        >
          {headingFont || 'Not specified'}
        </div>
      </div>
      <div className="brand-profile-panel__type-specimen">
        <span className="brand-profile-panel__type-label">Body</span>
        <div
          className="brand-profile-panel__type-preview brand-profile-panel__type-preview--body"
          style={{ fontFamily: bodyFont || 'inherit' }}
        >
          {bodyFont || 'Not specified'}
        </div>
      </div>
      {getArray(fallbackFonts).length ? (
        <div className="brand-profile-panel__type-fallbacks">
          <span>Fallbacks</span>
          <span>{getArray(fallbackFonts).join(', ')}</span>
        </div>
      ) : null}
    </div>
  )
}

const VideoFormatCards = ({ defaults }) => {
  const aspectRatios = getArray(defaults.aspect_ratios)
  const labels = ['Landscape', 'Portrait', 'Square']
  const icons = ['laptop', 'mobile', 'appstore']

  if (!aspectRatios.length) {
    return <span className="brand-profile-panel__muted">No formats configured</span>
  }

  return (
    <div className="brand-profile-panel__formats">
      {aspectRatios.map((ratio, index) => (
        <div className="brand-profile-panel__format-card" key={ratio}>
          <Icon type={icons[index] || 'picture'} />
          <strong>{ratio}</strong>
          <span>{labels[index] || 'Format'}</span>
        </div>
      ))}
      {defaults.max_duration_seconds ? (
        <div className="brand-profile-panel__format-card brand-profile-panel__format-card--duration">
          <Icon type="clock-circle" />
          <strong>{defaults.max_duration_seconds}s</strong>
          <span>Max duration</span>
        </div>
      ) : null}
    </div>
  )
}

const SummaryBlock = ({ summary }) => {
  const paragraphs = splitParagraphs(summary)
  const midpoint = Math.ceil(paragraphs.length / 2)
  const columns = [
    paragraphs.slice(0, midpoint),
    paragraphs.slice(midpoint)
  ].filter(column => column.length)

  return (
    <div className="brand-profile-panel__summary-columns">
      {columns.map((column, columnIndex) => (
        <div key={columnIndex}>
          {column.map((paragraph, index) => (
            <p key={`${columnIndex}-${index}`}>{paragraph}</p>
          ))}
        </div>
      ))}
    </div>
  )
}

const BrandProfilePanel = ({
  brandProfile,
  brandImageUrl,
  brandDocumentUrl,
  headerContent,
  onEditProfile,
  onDownloadDocument,
  isDownloadingDocument,
  onContinue
}) => {
  const profile = brandProfile || {}
  const personality = profile.brand_personality || {}
  const visualIdentity = profile.visual_identity || {}
  const contentGuidelines = profile.content_guidelines || {}
  const videoDefaults = profile.video_defaults || {}
  const primaryColors = getArray(visualIdentity.primary_colors)
  const secondaryColors = getArray(visualIdentity.secondary_colors)
  const allColors = [...primaryColors, ...secondaryColors]
  const valuesCount = getArray(profile.brand_values).length
  const aspectRatioCount = getArray(videoDefaults.aspect_ratios).length
  const additionalFields = Object.entries(profile).filter(
    ([key]) => !KNOWN_PROFILE_KEYS.includes(key)
  )

  return (
    <div className="brand-profile-panel">
      <div className="brand-profile-panel__content">
        {headerContent ? (
          <div className="brand-profile-panel__scroll-header">
            {headerContent}
          </div>
        ) : null}

        <div className="brand-profile-panel__page">
          <section className="brand-profile-panel__hero">
            <div className="brand-profile-panel__hero-top">
              <div className="brand-profile-panel__hero-text">
                <div className="brand-profile-panel__ready-badge">
                  <span />
                  Brand profile ready
                </div>
                <h2>{profile.organization_name || 'Your organization'}</h2>
                {profile.tagline ? (
                  <div className="brand-profile-panel__tagline">
                    &ldquo;{profile.tagline}&rdquo;
                  </div>
                ) : null}
              </div>
              <div className="brand-profile-panel__stats">
                <div>
                  <strong>{valuesCount || '-'}</strong>
                  <span>Values</span>
                </div>
                <div>
                  <strong>{allColors.length || '-'}</strong>
                  <span>Colors</span>
                </div>
                <div>
                  <strong>{aspectRatioCount || '-'}</strong>
                  <span>Formats</span>
                </div>
              </div>
            </div>

            <div className="brand-profile-panel__hero-bottom">
              <div className="brand-profile-panel__mission">
                <h4>Mission</h4>
                <p>{profile.mission || 'Not specified'}</p>
              </div>
              <div className="brand-profile-panel__brand-visual">
                {brandImageUrl ? (
                  <ImagePreview
                    key={brandImageUrl}
                    src={brandImageUrl}
                    alt="Generated brand visual"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div>
                    <Icon type="picture" />
                    <span>Brand visual is being prepared</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {profile.summary ? (
            <TextCard
              icon="file-text"
              title="Executive Summary"
              subtitle="High-level overview of the completed brand profile"
              className="brand-profile-panel__card--wide"
            >
              <SummaryBlock summary={profile.summary} />
            </TextCard>
          ) : null}

          <section className="brand-profile-panel__two-column">
            <TextCard
              icon="sound"
              title="Brand Personality"
              subtitle="Voice, tone, and communication guardrails"
            >
              <div className="brand-profile-panel__group">
                <h4>Tone</h4>
                <PillList items={personality.tone} />
              </div>
              <div className="brand-profile-panel__group">
                <h4>Voice Descriptors</h4>
                <PillList items={personality.voice_descriptors} />
              </div>
              <div className="brand-profile-panel__group">
                <h4>Avoid</h4>
                <PillList items={personality.avoid} tone="danger" />
              </div>
            </TextCard>

            <TextCard
              icon="star"
              title="Brand Values"
              subtitle="Principles embedded in all generated content"
              count={valuesCount}
            >
              <ValueGrid items={profile.brand_values} />
            </TextCard>
          </section>

          <TextCard
            icon="bg-colors"
            title="Color Palette"
            subtitle="Brand colors with usage guidance"
            count={allColors.length}
            className="brand-profile-panel__card--wide"
          >
            <ColorSwatches
              primaryColors={primaryColors}
              secondaryColors={secondaryColors}
            />
          </TextCard>

          <section className="brand-profile-panel__two-column">
            <TextCard
              icon="font-size"
              title="Typography"
              subtitle="Typefaces and hierarchy"
            >
              <TypographyDisplay typography={visualIdentity.typography || {}} />
            </TextCard>

            <TextCard
              icon="video-camera"
              title="Video Formats"
              subtitle="Aspect ratios and duration limits"
              count={aspectRatioCount}
            >
              <VideoFormatCards defaults={videoDefaults} />
            </TextCard>
          </section>

          <section className="brand-profile-panel__two-column">
            <TextCard
              icon="solution"
              title="Content Guidelines"
              subtitle="People, locations, wardrobe, music, and motion"
            >
              <DetailGrid data={contentGuidelines} />
            </TextCard>

            <TextCard
              icon="play-square"
              title="Video Style"
              subtitle="Reusable style defaults for all video content"
            >
              <DetailGrid
                data={{
                  intro_style: videoDefaults.intro_style,
                  outro_style: videoDefaults.outro_style,
                  lower_thirds_style: videoDefaults.lower_thirds_style,
                  transition_preference: videoDefaults.transition_preference
                }}
              />
            </TextCard>
          </section>

          <section className="brand-profile-panel__two-column">
            <TextCard
              icon="team"
              title="Target Audience"
              subtitle="Who this brand speaks to most directly"
            >
              <div className="brand-profile-panel__body-copy">
                {splitParagraphs(profile.target_audience).map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </TextCard>

            <TextCard
              icon="trophy"
              title="Differentiation"
              subtitle="What makes this brand distinct in the market"
            >
              <div className="brand-profile-panel__body-copy">
                {splitParagraphs(profile.competitor_differentiation).map(
                  (paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  )
                )}
              </div>
            </TextCard>
          </section>

          <section className="brand-profile-panel__two-column">
            <TextCard
              icon="picture"
              title="Logo &amp; Imagery"
              subtitle="Visual rules for brand assets and scenes"
            >
              <DetailGrid
                data={{
                  logo_usage: visualIdentity.logo_usage,
                  imagery_style: visualIdentity.imagery_style
                }}
              />
            </TextCard>

            <TextCard
              icon="info-circle"
              title="Color Details"
              subtitle="Detailed usage notes for each brand color"
            >
              <DetailGrid
                data={allColors.reduce(
                  (acc, color) => ({
                    ...acc,
                    [color.name || color.hex || 'color']: color.usage || color.hex
                  }),
                  {}
                )}
              />
            </TextCard>
          </section>

          {additionalFields.length ? (
            <TextCard
              icon="database"
              title="Additional Fields"
              subtitle="Other details captured for this brand profile"
              className="brand-profile-panel__card--wide"
            >
              <DetailGrid
                data={additionalFields.reduce(
                  (acc, [key, value]) => ({ ...acc, [key]: value }),
                  {}
                )}
              />
            </TextCard>
          ) : null}
        </div>

        <footer className="brand-profile-panel__footer">
          <div className="brand-profile-panel__footer-actions">
            <Button
              className="brand-profile-panel__secondary-btn"
              onClick={onEditProfile}
            >
              <Icon type="edit" />
              Edit profile
            </Button>
            <Button
              className="brand-profile-panel__download-btn"
              loading={isDownloadingDocument}
              disabled={!brandDocumentUrl && !onDownloadDocument}
              onClick={onDownloadDocument}
            >
              <Icon type="download" />
              Download Brand Document
            </Button>
          </div>
          <Button
            className="brand-profile-panel__continue-btn"
            type="primary"
            onClick={onContinue}
          >
            Continue to AI video
            <Icon type="arrow-right" />
          </Button>
        </footer>
      </div>
    </div>
  )
}

export default BrandProfilePanel
